import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { Label, Button } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActionBar } from '../action-bar';
import { EditableFluentList } from '../../fluent-list-component';
import { getCategoryTargetStyles } from './styles/category-target-style';
import { CategoryTargetItem } from './category-target-item';
import { DefaultBidDescription } from '../default-bid-description';
import { useDefaultBid } from '../../utils';

export const CategoryTarget = ({
  data, onDataChange, onCancel, preloadedCategories, domainName, languageCode,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getCategoryTargetStyles);
  const defaultBid = useDefaultBid();

  const [itemKeys, setItemKeys] = useState({});
  const [alwaysShowError, setAlwaysShowError] = useState(false);

  useEffect(() => {
    onDataChange({ ...data, bufferItems: [...data.items] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newKeys = _.reduce(data.bufferItems, (acc, item) => {
      const key = item.conditions && item.conditions[0].ArgumentOperand;
      acc[key] = _.result(acc, key, 0) + 1;
      return acc;
    }, {});
    setItemKeys(newKeys);
  }, [data.bufferItems]);

  const verifyData = useCallback(() => (
    _.every(itemKeys, (value, index) => value <= 1 && index !== 'undefined' && !_.isEmpty(index)) && _.every(data.bufferItems, item => item.isValid)
  ), [data.bufferItems, itemKeys]);

  const onListItemChange = useCallback(index => (item) => {
    const newBufferItems = data.bufferItems.slice();
    newBufferItems[index] = item;
    onDataChange({ ...data, bufferItems: newBufferItems });
  }, [data, onDataChange]);

  const onListItemRemove = useCallback(index => () => {
    const newBufferItems = data.bufferItems.slice();
    newBufferItems.splice(index, 1);

    onDataChange({ ...data, bufferItems: newBufferItems });
  }, [data, onDataChange]);

  const onRemoveAll = useCallback(() => {
    onDataChange({ ...data, bufferItems: [] });
  }, [data, onDataChange]);

  const onAddButtonClick = useCallback(() => {
    onDataChange({ ...data, bufferItems: [...data.bufferItems, {}] });
  }, [data, onDataChange]);

  const onApply = useCallback(() => {
    setAlwaysShowError(true);
    if (!verifyData()) {
      return;
    }
    const newItems = _.map(data.bufferItems, item => ({
      ...item,
      Bid: item.Bid || defaultBid,
    }));
    onDataChange({ ...data, items: newItems });
    onCancel();
  }, [data, onCancel, onDataChange, verifyData, defaultBid]);

  const onRenderHeader = useCallback(() => (
    <div className={classnames(classes.listHeader, 'list-header')}>
      <Label className="category-label">{i18n.getString(_TL_('Category'))}</Label>
      <Label className="bid">{i18n.getString(_TL_('Bid'))}</Label>
      <Button
        style={{
          background: 'transparent',
          border: 'none',
          alignItems: 'start',
          marginLeft: '10px',
        }}
        onClick={onRemoveAll}
      >
        {i18n.getString(_TL_('Remove all'))}
      </Button>
    </div>
  ), [classes.listHeader, i18n, onRemoveAll]);

  const onRenderItem = useCallback(({ item, onItemChange }) => (
    <CategoryTargetItem
      disabledCategories={itemKeys}
      i18n={i18n}
      item={item}
      onItemChange={onItemChange}
      preloadedCategories={preloadedCategories}
      domainName={domainName}
      languageCode={languageCode}
      alwaysShowError={alwaysShowError}
    />
  ), [domainName, i18n, itemKeys, languageCode, preloadedCategories, alwaysShowError]);

  const onRenderFooter = useCallback(() => (<DefaultBidDescription />), []);

  return (
    <div>
      <EditableFluentList
        items={data.bufferItems}
        onItemChange={onListItemChange}
        onItemRemove={onListItemRemove}
        onRenderHeader={onRenderHeader}
        onRenderItem={onRenderItem}
        onRenderFooter={onRenderFooter}
        addButtonText={i18n.getString(_TL_('Add category'))}
        onAddButtonClick={onAddButtonClick}
      />
      <ActionBar onApply={onApply} onCancel={onCancel} />
    </div>
  );
};

CategoryTarget.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onDataChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})),
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
};

CategoryTarget.defaultProps = {
  preloadedCategories: null,
};
